import { motion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';

import { GAME_MODE } from '~/interfaces';
import { getLane, getPlayerCanvas, getScoreGutter } from '~/modules/game-engine/drawing/calculate-data';
import { playerTextColor } from '~/modules/game-engine/drawing/styles';
import GameState from '~/modules/game-engine/game-state/game-state';
import { PlayerNumber } from '~/modules/players/player-number';
import PlayersManager from '~/modules/players/players-manager';
import tuple from '~/modules/utils/tuple';
import { FeedbackPill, ScoreRow } from '~/routes/game/singing/game-overlay/components/score-card';
import ScoreCard from '~/routes/game/singing/game-overlay/components/score-card';
import ScoreText from '~/routes/game/singing/game-overlay/components/score-text';
import { getComboDisplay } from '~/routes/game/singing/game-overlay/helpers/combo-display';
import { ofFrame } from '~/routes/game/singing/game-overlay/helpers/frame-scale';
import getPlayerScoreData from '~/routes/game/singing/game-overlay/helpers/get-player-score-data';
import { getMicrophoneHolder } from '~/routes/game/singing/game-overlay/helpers/pass-the-mic-turn';
import { rankPlayers } from '~/routes/game/singing/game-overlay/helpers/rank-players';
import { LeadState, settleLead } from '~/routes/game/singing/game-overlay/helpers/settle-lead';
import useNoteFeedback from '~/routes/game/singing/game-overlay/hooks/use-note-feedback';
import { cn } from '~/utils/cn';

/** How many singers each size of the column holds: full cards, compact cards, then one-line rows. */
const COMPACT_FROM_PLAYERS = 3;
const ROWS_FROM_PLAYERS = 5;

/** Where in its lane a duet's card sits, as a share of the lane's height. */
const CARD_OFFSET_IN_LANE = 0.15;

/** Space either side of the scores within their gutter, as a share of the gutter. */
const GUTTER_MARGIN = 0.07;

interface Props {
  /** The overlay canvas's own pixel size, which the lane geometry is expressed in. */
  width: number;
  height: number;
  /** Room the lyrics take at the top and bottom, as passed to the canvas drawer. */
  paddingVertical: number;
  /** Online mode: who leads the room. The local game settles the lead from this device's scores. */
  leadingPlayerNumber?: PlayerNumber | null;
}

function useSettledLeader(leader: PlayerNumber | null) {
  const [settled, setSettled] = useState<PlayerNumber | null>(null);
  const state = useRef<LeadState | null>(null);

  useEffect(() => {
    const look = () => {
      const result = settleLead(state.current, leader, Date.now());
      state.current = result.state;
      setSettled((current) => (current === result.settled ? current : result.settled));
    };

    look();
    const holding = setInterval(look, 100);

    return () => clearInterval(holding);
  }, [leader]);

  return settled;
}

/**
 * The team's colours as hard bands, one per singer, in player order. Hard stops rather than a blend
 * so every singer still reads as one.
 */
const coopBarBackground = (colors: string[]) => {
  if (colors.length < 2) return colors[0];

  const share = 100 / colors.length;
  const bands = colors.flatMap((color, index) => [`${color} ${index * share}%`, `${color} ${(index + 1) * share}%`]);

  return `linear-gradient(${bands.join(', ')})`;
};

/**
 * Every singer's score in one ranked column beside the lane they share. A duet keeps a card beside
 * each of its two lanes instead, since there the pairing of score and part is the point.
 *
 * Geometry comes from `getPlayerCanvas`, the same function the canvas draws the lanes with, and is
 * turned into percentages: the canvas is stretched to fill its box, so a share of its pixel size is
 * the same share of what is on screen at any resolution.
 */
export default function PlayerScores({ width, height, paddingVertical, leadingPlayerNumber }: Props) {
  'use no memo'; // React Compiler: reads the GameState/PlayersManager singletons directly during render, so a cached result would freeze the score at whatever it was on the first frame.
  // Only the round's singers, so the column and the lanes agree on who is singing.
  const players = PlayersManager.getPlayers().filter((player) => GameState.getPlayer(player.number));
  const noteStates = useNoteFeedback();
  const isCoop = GameState.getSingSetup()?.mode === GAME_MODE.CO_OP;
  const separateLanes = !GameState.isMergedTrack();

  const scores = players.map((player) => tuple([player.number, GameState.getPlayerScore(player.number)]));
  const contested = players.find((player) => getPlayerScoreData(scores, player.number).isFirst);
  const settledLeader = useSettledLeader(contested?.number ?? null);
  const leader = leadingPlayerNumber === undefined ? settledLeader : leadingPlayerNumber;

  const numbers = players.map((player) => player.number);
  const laneTop = (number: PlayerNumber) => {
    const lane = getPlayerCanvas({
      canvas: { width, height },
      ...getLane(numbers, number, separateLanes),
      paddingVertical,
    });

    return `${((lane.baseY + lane.height * CARD_OFFSET_IN_LANE) / height) * 100}%`;
  };
  // Held inside the gutter the lanes leave on the right, so no score ever covers a note.
  const gutter = getScoreGutter({ width, height });
  const gutterStyle = {
    right: `${((gutter * GUTTER_MARGIN) / width) * 100}%`,
    width: `${((gutter * (1 - 2 * GUTTER_MARGIN)) / width) * 100}%`,
  };
  const columnStyle = {
    ...gutterStyle,
    top: `${(paddingVertical / height) * 100}%`,
    bottom: `${(paddingVertical / height) * 100}%`,
  };

  const colorOf = playerTextColor;
  const holdingMicrophone = getMicrophoneHolder();
  const now = Date.now();

  const comboOf = (number: PlayerNumber) => {
    const combo = noteStates[number]?.combo;

    return getComboDisplay({ combo: combo?.current ?? 0, lostAt: combo?.lostAt ?? null, now });
  };

  if (isCoop) {
    // One score for the team, so one card - but the feedback still belongs to whoever sang it.
    return (
      <div className="pointer-events-none absolute inset-0 z-10">
        <div className="absolute flex flex-col items-end gap-3" style={columnStyle}>
          <div
            className="flex items-center justify-end gap-3.5"
            data-test="players-score"
            data-score={Math.floor(GameState.getPlayerScore(0))}>
            <div className="flex flex-col items-end gap-0.5">
              <span
                className="stroke-text text-right font-bold tracking-[.04em] uppercase"
                style={{ fontSize: ofFrame(players.length > 4 ? 18 : 22), lineHeight: 1.2 }}>
                {players.map((player, index) => (
                  <span key={player.number}>
                    {index > 0 && <span className="opacity-60"> &amp; </span>}
                    <span className="ph-no-capture" style={{ color: colorOf(player.number) }}>
                      {player.getName()}
                    </span>
                  </span>
                ))}
              </span>
              <span
                className="stroke-text leading-none font-bold text-white tabular-nums"
                style={{ fontSize: ofFrame(52) }}>
                <ScoreText score={GameState.getPlayerScore(0)} />
              </span>
              <span
                className="stroke-text tracking-[.08em] whitespace-nowrap text-white/45 uppercase"
                style={{ fontSize: ofFrame(15) }}>
                one shared score
              </span>
            </div>
            <span
              className="w-2 shrink-0 rounded"
              style={{
                height: ofFrame(64),
                // Every singer on the team gets a band, not just the first two - a two-colour bar
                // beside more names reads as a bug.
                background: coopBarBackground(players.map((player) => colorOf(player.number))),
              }}
            />
          </div>

          {players.map((player) => (
            <div
              key={player.number}
              className="flex items-center gap-3"
              style={{ height: ofFrame(players.length > 4 ? 30 : 40) }}
              data-test={`score-feedback-${player.number}`}>
              {noteStates[player.number]?.feedback && (
                <FeedbackPill quality={noteStates[player.number]!.feedback!.quality} sizePx={18} />
              )}
              <span className="size-3 rounded-full" style={{ background: colorOf(player.number) }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const cardFor = (number: PlayerNumber, compact: boolean) => {
    const player = players.find((candidate) => candidate.number === number)!;

    return (
      <ScoreCard
        playerNumber={number}
        name={player.getName()}
        color={colorOf(number)}
        score={GameState.getPlayerScore(number)}
        isFirst={leader === number}
        compact={compact}
        combo={comboOf(number)}
        feedback={noteStates[number]?.feedback?.quality ?? null}
        singing={holdingMicrophone === number}
        dim={holdingMicrophone !== null && holdingMicrophone !== number}
      />
    );
  };

  if (separateLanes) {
    return (
      <div className="pointer-events-none absolute inset-0 z-10">
        {numbers.map((number) => (
          <div key={number} className="absolute flex justify-end" style={{ ...gutterStyle, top: laneTop(number) }}>
            {cardFor(number, players.length >= COMPACT_FROM_PLAYERS)}
          </div>
        ))}
      </div>
    );
  }

  const ranked = rankPlayers(scores);
  const asRows = players.length >= ROWS_FROM_PLAYERS;

  return (
    <div className="pointer-events-none absolute inset-0 z-10">
      <div
        className={cn('absolute flex flex-col', asRows ? 'gap-2' : 'items-end')}
        style={columnStyle}
        data-test="score-column">
        {ranked.map((number, index) => {
          const player = players.find((candidate) => candidate.number === number)!;

          return (
            // `layout` slides a singer to their new place when the ranking changes, rather than jumping.
            <motion.div key={number} layout transition={{ duration: 0.4 }} className={asRows ? 'w-full' : 'max-w-full'}>
              {asRows ? (
                <ScoreRow
                  playerNumber={number}
                  rank={index + 1}
                  name={player.getName()}
                  color={colorOf(number)}
                  score={GameState.getPlayerScore(number)}
                  isFirst={leader === number}
                  combo={comboOf(number)}
                  feedback={noteStates[number]?.feedback?.quality ?? null}
                  singing={holdingMicrophone === number}
                  dim={holdingMicrophone !== null && holdingMicrophone !== number}
                />
              ) : (
                cardFor(number, players.length >= COMPACT_FROM_PLAYERS)
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
